/**
 * Experimental Flux TTS mouth for HottieDock.
 * Speaks already-generated reply text via hottie-speak. Does not call the brain.
 */

import { env } from '@/config/env';
import { stopSpeakPlaybackTap, tapSpeakPlayback } from './voiceFeed';

/** Male Flux voices only. Hottie is a man; Flux has no AU male voice. */
export const FLUX_VOICES = [
  { id: 'flux-drew-en', label: 'Drew' },
  { id: 'flux-cole-en', label: 'Cole' },
  { id: 'flux-wade-en', label: 'Wade' },
  { id: 'flux-wes-en', label: 'Wes' },
  { id: 'flux-marcus-en', label: 'Marcus' },
] as const;

export const FLUX_SPEEDS = [0.85, 0.9, 0.95, 1.0, 1.05, 1.1, 1.15] as const;

export type FluxVoiceId = (typeof FLUX_VOICES)[number]['id'];
export type FluxSpeed = (typeof FLUX_SPEEDS)[number];

export const DEFAULT_VOICE: FluxVoiceId = 'flux-drew-en';
export const DEFAULT_SPEED: FluxSpeed = 0.85;
export const DEFAULT_EXPRESSIVITY = 0;

const STORAGE_KEY = 'hottr.hottie.speak';

export interface SpeakPrefs {
  enabled: boolean;
  model: FluxVoiceId;
  speed: FluxSpeed;
}

export const DEFAULT_SPEAK_PREFS: SpeakPrefs = {
  enabled: false,
  model: DEFAULT_VOICE,
  speed: DEFAULT_SPEED,
};

export function isFluxVoice(value: unknown): value is FluxVoiceId {
  return typeof value === 'string' && FLUX_VOICES.some((v) => v.id === value);
}

export function isFluxSpeed(value: unknown): value is FluxSpeed {
  return typeof value === 'number' && (FLUX_SPEEDS as readonly number[]).includes(value);
}

export function loadSpeakPrefs(): SpeakPrefs {
  if (typeof window === 'undefined') return { ...DEFAULT_SPEAK_PREFS };
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return { ...DEFAULT_SPEAK_PREFS };
    const parsed = JSON.parse(raw) as Partial<SpeakPrefs>;
    return {
      enabled: parsed.enabled === true,
      model: isFluxVoice(parsed.model) ? parsed.model : DEFAULT_VOICE,
      speed: isFluxSpeed(parsed.speed) ? parsed.speed : DEFAULT_SPEED,
    };
  } catch {
    return { ...DEFAULT_SPEAK_PREFS };
  }
}

export function saveSpeakPrefs(prefs: SpeakPrefs): void {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
}

let currentAudio: HTMLAudioElement | null = null;
let currentUrl: string | null = null;
let speakEnded: Promise<void> | null = null;
let resolveSpeakEnded: (() => void) | null = null;

function armSpeakEnded(): void {
  speakEnded = new Promise((resolve) => {
    resolveSpeakEnded = resolve;
  });
}

function finishSpeakEnded(): void {
  resolveSpeakEnded?.();
  resolveSpeakEnded = null;
  speakEnded = null;
}

export function stopSpeak(): void {
  currentAudio?.pause();
  stopSpeakPlaybackTap();
  currentAudio = null;
  if (currentUrl) {
    URL.revokeObjectURL(currentUrl);
    currentUrl = null;
  }
  finishSpeakEnded();
}

/** Resolves when the current mouth finishes or is stopped. */
export function waitForSpeakEnd(): Promise<void> {
  return speakEnded ?? Promise.resolve();
}

export interface SpeakReplyArgs {
  text: string;
  accessToken: string;
  model: FluxVoiceId;
  speed: FluxSpeed;
}

export async function speakReply(args: SpeakReplyArgs): Promise<{ ok: true } | { ok: false; error: string }> {
  const text = args.text.trim();
  if (!text) return { ok: false, error: "couldn't speak that" };
  if (!env.supabaseUrl || !env.supabaseAnonKey) {
    return { ok: false, error: "couldn't speak that" };
  }

  let res: Response;
  try {
    res = await fetch(`${env.supabaseUrl}/functions/v1/hottie-speak`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${args.accessToken}`,
        apikey: env.supabaseAnonKey,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        text,
        model: args.model,
        speed: args.speed,
        expressivity: DEFAULT_EXPRESSIVITY,
      }),
    });
  } catch {
    return { ok: false, error: "couldn't speak that" };
  }

  if (!res.ok) return { ok: false, error: "couldn't speak that" };

  const blob = await res.blob();
  if (!blob.size) return { ok: false, error: "couldn't speak that" };

  stopSpeak();
  currentUrl = URL.createObjectURL(blob);
  currentAudio = new Audio(currentUrl);
  armSpeakEnded();
  currentAudio.onended = () => stopSpeak();
  currentAudio.onerror = () => stopSpeak();
  tapSpeakPlayback(currentAudio);

  try {
    await currentAudio.play();
  } catch {
    stopSpeak();
    return { ok: false, error: "couldn't speak that" };
  }

  return { ok: true };
}
